import { useEffect, useMemo, useState } from "react";
import { formatDisplayName } from "../utils/displayName";
import { Link, useNavigate } from "react-router-dom";
import { ArrowDownNarrowWide, ArrowUpNarrowWide, Loader2, RefreshCw, Search } from "lucide-react";
import AdminDashboardLayout from "../components/AdminDashboardLayout";
import AdminMonthlyBar from "../components/AdminMonthlyBar";
import AdminNotifications from "../components/AdminNotifications";
import DonutStatusChart from "../components/DonutStatusChart";
import BrokerKPICards from "../components/BrokerKPICards";
import PortalFeedback from "../components/PortalFeedback";
import { supabase } from "../supabaseClient";
import { DASHBOARD_CARD_STATUSES, PROJECT_STATUSES, STATUS_TONE, getStatusSortIndex, normalizeProjectStatus } from "../utils/projectStatus";
import { formatExpectedTiming } from "../utils/timeFormat";
import { traceAsync } from "../utils/perfTrace";

const PANEL =
  "rounded-2xl border border-white/15 bg-white/70 backdrop-blur-md shadow-[0_10px_30px_rgba(0,0,0,0.08)]";

const normalizeStatus = (status) => normalizeProjectStatus(status);
const PRIORITY_BG = {
  Urgent: "rgba(211, 34, 43, 0.80)",
  Medium: "rgba(63, 63, 63, 0.80)",
  Low: "rgba(131, 131, 132, 0.80)",
};
const STATUS_ORDER = PROJECT_STATUSES;
const PRIORITY_ORDER = ["Urgent", "Medium", "Low"];
const emptyStatusCounts = () =>
  PROJECT_STATUSES.reduce((counts, status) => ({ ...counts, [status]: 0 }), {});

function StatusPill({ status }) {
  const s = normalizeStatus(status);
  const tone = STATUS_TONE[s] || "rgba(0,0,0,0.25)";
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium"
      style={{ color: tone, background: "rgba(0,0,0,0.035)", boxShadow: `inset 0 0 0 1px ${tone}22` }}
    >
      <span className="inline-block h-1.5 w-1.5 rounded-full" style={{ backgroundColor: tone }} />
      {s || "—"}
    </span>
  );
}

function PriorityPill({ priority }) {
  const bg = PRIORITY_BG[priority] || "rgba(0,0,0,0.06)";
  return (
    <span
      className="inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold"
      style={{ background: bg, color: "#FFFFFF" }}
    >
      {priority || "—"}
    </span>
  );
}

export default function AdminHome() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorText, setErrorText] = useState("");
  const [lastUpdated, setLastUpdated] = useState(null);
  const [timeRange, setTimeRange] = useState("30d");
  const [trackerSort, setTrackerSort] = useState({ by: "modified", dir: "desc" });
  const [tailSearch, setTailSearch] = useState("");

  const loadProjects = async () => {
    setLoading(true);
    setErrorText("");
    const { data, error } = await traceAsync(
      "dashboard.admin.projects",
      () => supabase
        .from("project_status")
        .select("id,tail_number,current_status,priority,assigned_pilot,expected_datetime,timezone,updated_at,broker_intake(id,created_at,services,contact_name,contact_email,broker_accounts(name))")
        .order("updated_at", { ascending: false }),
      {}
    );

    if (error) {
      setErrorText(error.message || "Could not load admin projects.");
      setProjects([]);
      setLoading(false);
      return;
    }

    setProjects(data || []);
    setLastUpdated(new Date());
    setLoading(false);
  };

  useEffect(() => {
    loadProjects();
  }, []);

  const filteredByRange = useMemo(() => {
    if (timeRange === "All Time" || timeRange === "all") return projects;
    const days = Number(String(timeRange).replace("d", ""));
    if (!days) return projects;
    const start = new Date();
    start.setDate(start.getDate() - days);
    return projects.filter((row) => {
      const createdAt = row?.broker_intake?.created_at;
      return createdAt && new Date(createdAt) >= start;
    });
  }, [projects, timeRange]);

  const counts = useMemo(() => {
    const next = emptyStatusCounts();
    filteredByRange.forEach((row) => {
      const status = normalizeStatus(row.current_status);
      if (next[status] !== undefined) next[status] += 1;
    });
    return next;
  }, [filteredByRange]);

  const cardCounts = useMemo(() => {
    const allTimeOpenCounts = { Requested: 0, Scheduled: 0 };
    projects.forEach((row) => {
      const status = normalizeStatus(row.current_status);
      if (DASHBOARD_CARD_STATUSES.slice(0, 2).includes(status)) {
        allTimeOpenCounts[status] += 1;
      }
    });
    return {
      ...counts,
      Requested: allTimeOpenCounts.Requested,
      Scheduled: allTimeOpenCounts.Scheduled,
    };
  }, [counts, projects]);

  const toggleTrackerSort = (by) => {
    setTrackerSort((current) =>
      current.by === by ? { by, dir: current.dir === "asc" ? "desc" : "asc" } : { by, dir: "asc" }
    );
  };

  const getModifiedTime = (row) =>
    new Date(row?.updated_at || row?.broker_intake?.created_at || 0).getTime();

  const trackerRows = useMemo(() => {
    const byLastModified = [...projects]
      .sort((a, b) => {
        return getModifiedTime(b) - getModifiedTime(a);
      })
      .slice(0, 5);

    const direction = trackerSort.dir === "asc" ? 1 : -1;
    return byLastModified.sort((a, b) => {
      if (trackerSort.by === "tail") {
        return String(a.tail_number || "").localeCompare(String(b.tail_number || ""), undefined, {
          numeric: true,
          sensitivity: "base",
        }) * direction;
      }
      if (trackerSort.by === "broker") {
        const aBroker = a.broker_intake?.broker_accounts?.name || a.broker_intake?.contact_name || "";
        const bBroker = b.broker_intake?.broker_accounts?.name || b.broker_intake?.contact_name || "";
        return aBroker.localeCompare(bBroker, undefined, { sensitivity: "base" }) * direction;
      }
      if (trackerSort.by === "priority") {
        return (PRIORITY_ORDER.indexOf(a.priority || "") - PRIORITY_ORDER.indexOf(b.priority || "")) * direction;
      }
      if (trackerSort.by === "status") {
        return (getStatusSortIndex(a.current_status) - getStatusSortIndex(b.current_status)) * direction;
      }
      if (trackerSort.by === "assigned") {
        return String(a.assigned_pilot || "").localeCompare(String(b.assigned_pilot || ""), undefined, {
          sensitivity: "base",
        }) * direction;
      }
      if (trackerSort.by === "expected") {
        return (new Date(a.expected_datetime || 0).getTime() - new Date(b.expected_datetime || 0).getTime()) * direction;
      }
      if (trackerSort.by === "modified") {
        return (getModifiedTime(a) - getModifiedTime(b)) * direction;
      }
      const aTime = new Date(a?.broker_intake?.created_at || 0).getTime();
      const bTime = new Date(b?.broker_intake?.created_at || 0).getTime();
      return (aTime - bTime) * direction;
    });
  }, [projects, trackerSort]);

  const TrackerTh = ({ id, label }) => {
    const active = trackerSort.by === id;
    const icon = active
      ? trackerSort.dir === "asc"
        ? <ArrowUpNarrowWide className="h-4 w-4" />
        : <ArrowDownNarrowWide className="h-4 w-4" />
      : <ArrowDownNarrowWide className="h-4 w-4 opacity-40" />;

    return (
      <th className="px-4 py-3 font-medium border-r border-black/10 last:border-r-0">
        <button
          type="button"
          onClick={() => toggleTrackerSort(id)}
          className="inline-flex items-center gap-1.5 text-xs tracking-wide hover:text-black"
        >
          {label}
          <span className="opacity-70">{icon}</span>
        </button>
      </th>
    );
  };

  return (
    <AdminDashboardLayout>
      <div className="space-y-8">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-3xl font-extrabold tracking-tight">Admin Dashboard</h1>
            <p className="text-xs text-black/60 mt-1">
              {lastUpdated ? `Last updated: ${lastUpdated.toLocaleString()}` : "All broker requests"}
            </p>
          </div>
          <div className="flex w-full items-center gap-3 md:w-auto">
            <form
              className={`${PANEL} flex h-10 w-full items-center gap-3 px-4 md:w-80`}
              onSubmit={(event) => {
                event.preventDefault();
                const value = tailSearch.trim();
                navigate(value ? `/admin/projects?q=${encodeURIComponent(value)}` : "/admin/projects");
              }}
            >
              <Search className="h-5 w-5 shrink-0 text-black/35" strokeWidth={2} />
              <input
                value={tailSearch}
                onChange={(event) => setTailSearch(event.target.value)}
                placeholder="Search tail #"
                className="w-full bg-transparent text-sm outline-none placeholder-black/45"
                aria-label="Search tail number"
              />
            </form>
            <AdminNotifications />
            <button
              onClick={loadProjects}
              className={`${PANEL} inline-flex h-10 w-10 shrink-0 items-center justify-center hover:bg-white transition`}
              type="button"
              aria-label="Refresh dashboard"
              title="Refresh"
            >
              {loading ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <RefreshCw className="h-5 w-5" />
              )}
            </button>
          </div>
        </div>

        {errorText && <PortalFeedback type="error">{errorText}</PortalFeedback>}

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          <div className={`${PANEL} p-5`}>
            <DonutStatusChart counts={counts} activeRange={timeRange} onRangeChange={setTimeRange} />
          </div>
          <div className="flex flex-col gap-6">
            <BrokerKPICards counts={cardCounts} loading={loading} projectPath="/admin/projects" />
            <div className={`${PANEL} p-5`}>
              <h2 className="text-lg font-semibold">Requests by Month</h2>
              <AdminMonthlyBar projects={projects} heightClass="h-52 md:h-52" />
            </div>
          </div>
        </div>

        <div className={`${PANEL} overflow-hidden`}>
          <div className="flex items-end justify-between bg-[#f1f2f4] px-4 py-3">
            <div>
              <h2 className="text-lg font-semibold">Project Tracker</h2>
              <p className="text-xs text-black/55">
                Showing 5 last modified projects across all brokers • sorted by{" "}
                <span className="font-medium">{trackerSort.by === "modified" ? "last modified" : trackerSort.by}</span> ({trackerSort.dir})
              </p>
            </div>
            <Link to="/admin/projects" className="text-xs font-medium text-black/70 underline underline-offset-4 hover:text-black">
              View all →
            </Link>
          </div>
          <div className="overflow-auto bg-white">
            <table className="w-full text-left text-sm">
              <thead className="sticky top-0 bg-white">
                <tr className="border-b border-black/10 text-xs text-black/55">
                  <TrackerTh id="tail" label="Tail #" />
                  <TrackerTh id="broker" label="Broker" />
                  <TrackerTh id="priority" label="Priority" />
                  <TrackerTh id="status" label="Status" />
                  <TrackerTh id="assigned" label="Assigned" />
                  <TrackerTh id="expected" label="Expected" />
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={6} className="px-4 py-6 text-center text-black/50">Loading…</td></tr>}
                {!loading && trackerRows.length === 0 && <tr><td colSpan={6} className="px-4 py-6 text-center text-black/50">No projects found.</td></tr>}
                {!loading && trackerRows.map((row) => (
                  <tr
                    key={row.id}
                    tabIndex={0}
                    onClick={() => navigate(`/admin/projects/${row.id}`)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        navigate(`/admin/projects/${row.id}`);
                      }
                    }}
                    className="cursor-pointer border-b border-black/5 transition hover:bg-black/[0.02] focus:bg-black/[0.03] focus:outline-none"
                  >
                    <td className="px-4 py-4 font-medium border-r border-black/10">
                      {row.tail_number || "—"}
                    </td>
                    <td className="px-4 py-4 border-r border-black/10">{formatDisplayName(row.broker_intake?.broker_accounts?.name || row.broker_intake?.contact_name) || "—"}</td>
                    <td className="px-4 py-4 border-r border-black/10"><PriorityPill priority={row.priority} /></td>
                    <td className="px-4 py-4 border-r border-black/10"><StatusPill status={row.current_status} /></td>
                    <td className="px-4 py-4 border-r border-black/10">{formatDisplayName(row.assigned_pilot) || "—"}</td>
                    <td className="px-4 py-4">{formatExpectedTiming(row.expected_datetime, row.timezone)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </AdminDashboardLayout>
  );
}
